import React, { useCallback, useEffect, useRef, useState } from "react";
import { FaCircleXmark } from "react-icons/fa6";
import InfiniteScrollComponent from "../../../Components/InfiniteScrollComponent";
import ModalContainer from "../../../Components/ModalContainer";
import {
  useLazyGetActivityUsersQuery,
  useLazyGetSubscriptionUsersQuery,
} from "../../../Data/services/engagementApi";
import { ErrorToast } from "../../../Hooks/useToast";
import {
  STATUS_COLORS,
  campaignLabel,
  formatRelativeTime,
  formatUserDisplay,
  getLastNotification,
  getSegmentGroup,
  segmentLabel,
} from "../constants/labels";
import styles from "../styles/modal.module.scss";
import { extractLinks, hasNextPage } from "../utils";
import UsersTableSkeleton from "./UsersTableSkeleton";

const PAGE_SIZE = 20;

export default function SegmentUsersModal({ segmentKey, open, onClose }) {
  const scrollRef = useRef(null);
  const pageRef = useRef(1);
  const [lastPageSize, setLastPageSize] = useState(null);

  const [fetchSubscriptionUsers, subscriptionState] =
    useLazyGetSubscriptionUsersQuery();
  const [fetchActivityUsers, activityState] = useLazyGetActivityUsersQuery();

  const group = getSegmentGroup(segmentKey);
  const activeState =
    group === "subscription" ? subscriptionState : activityState;

  const { data, isLoading, isFetching, isError, isSuccess } = activeState;

  const loadPage = useCallback(
    async (page, limit = PAGE_SIZE) => {
      if (!segmentKey || !group) return;

      const request =
        group === "subscription"
          ? fetchSubscriptionUsers({
              filter: segmentKey,
              page,
              limit,
            })
          : fetchActivityUsers({
              filter: segmentKey,
              page,
              limit,
            });

      try {
        const response = await request.unwrap();
        const pageUsers = response?.data?.users ?? [];
        setLastPageSize(Array.isArray(pageUsers) ? pageUsers.length : 0);
        pageRef.current = page;
      } catch (err) {
        ErrorToast(err?.data?.message || "Failed to load users");
        throw err;
      }
    },
    [segmentKey, group, fetchSubscriptionUsers, fetchActivityUsers]
  );

  useEffect(() => {
    if (!open || !segmentKey || !group) return;
    pageRef.current = 1;
    setLastPageSize(null);
    loadPage(1, PAGE_SIZE);
  }, [open, segmentKey, group, loadPage]);

  const links = extractLinks(data);
  const payload = data?.data ?? {};
  const users = Array.isArray(payload.users) ? payload.users : [];
  const totalRecords = links.total_records;
  const perPage = links.per_page || PAGE_SIZE;
  const canLoadMore = hasNextPage(links, {
    loadedCount: users.length,
    lastPageSize,
    perPage,
  });

  const handleNext = useCallback(async () => {
    if (isFetching || !canLoadMore) return;
    const nextPage =
      links.next > links.current ? links.next : pageRef.current + 1;
    await loadPage(nextPage, perPage);
  }, [isFetching, canLoadMore, links.next, links.current, loadPage, perPage]);

  if (!open) return null;

  const hasNext =
    Boolean(group) && !isError && users.length > 0 && canLoadMore;
  const showInitialSkeleton = (isLoading || isFetching) && users.length === 0;
  const showEmpty = isSuccess && !isFetching && users.length === 0;
  const isLoadingMore = isFetching && users.length > 0;

  return (
    <ModalContainer handleClose={onClose}>
      <div className={styles.modalPanel}>
        <div className={styles.modalHeader}>
          <div>
            <p className={styles.modalTitle}>{segmentLabel(segmentKey)}</p>
            <p className={styles.modalSubtitle}>
              {group
                ? `${totalRecords || users.length} users · page ${
                    links.current || 1
                  } · showing ${users.length}`
                : "Unsupported filter"}
            </p>
          </div>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={onClose}
            aria-label="Close"
          >
            <FaCircleXmark />
          </button>
        </div>

        <div className={styles.modalBody} ref={scrollRef}>
          {!group || (isError && users.length === 0) ? (
            <p className="text-danger m-0">Failed to load users.</p>
          ) : showEmpty ? (
            <div className={styles.emptyState}>No users in this segment.</div>
          ) : showInitialSkeleton ? (
            <UsersTableSkeleton rows={8} columns={4} />
          ) : (
            <InfiniteScrollComponent
              next={handleNext}
              hasNext={hasNext}
              isLoading={isLoadingMore}
              rootRef={scrollRef}
              skeleton={<UsersTableSkeleton rows={3} columns={4} />}
            >
              <div className={styles.tableWrap}>
                <table className={styles.dataTable}>
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Last active</th>
                      <th>Push</th>
                      <th>Last notification</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user, index) => {
                      const display = formatUserDisplay(user, index);
                      const lastNotif = getLastNotification(user);
                      const status = (lastNotif?.status || "").toLowerCase();
                      const hasFcm = Boolean(user.has_fcm ?? user.fcm_token);

                      return (
                        <tr key={user.slug || `${display.title}-${index}`}>
                          <td>
                            <div className={styles.userCell}>
                              <span className={styles.userName}>
                                {display.title}
                              </span>
                              {display.subtitle ? (
                                <span className={styles.userMeta}>
                                  {display.subtitle}
                                </span>
                              ) : null}
                            </div>
                          </td>
                          <td>{formatRelativeTime(user.last_active_at)}</td>
                          <td>
                            <span
                              className={`${styles.badge} ${
                                hasFcm
                                  ? styles.badgeSent
                                  : styles.badgeSkipped
                              }`}
                            >
                              {hasFcm ? "Eligible" : "No FCM"}
                            </span>
                          </td>
                          <td>
                            {lastNotif ? (
                              <div className={styles.userCell}>
                                <span
                                  className={styles.badge}
                                  style={{
                                    backgroundColor:
                                      STATUS_COLORS[status] || "#6c757d",
                                    alignSelf: "flex-start",
                                  }}
                                >
                                  {status || "—"}
                                </span>
                                <span className={styles.userMeta}>
                                  {campaignLabel(lastNotif.campaign_slug)} ·{" "}
                                  {formatRelativeTime(lastNotif.sent_at)}
                                </span>
                              </div>
                            ) : (
                              "—"
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </InfiniteScrollComponent>
          )}
        </div>
      </div>
    </ModalContainer>
  );
}
